import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@modules/lib/utils"
import dayjs from "dayjs"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useState } from "react"

type MonthPickerCalendarProps = {
  /** Currently selected year */
  year: number
  /** Currently selected month (0-indexed: 0 = January) — used for highlighting */
  month: number
  /** Called when user picks a month. monthIndex is 0-indexed - dayjs standard */
  onSelectMonth: (monthIndex: number) => void
  /** Called when the internal picker year changes */
  onYearChange?: (year: number) => void
  /** Minimum selectable date. Months before this are disabled. */
  minDate?: Date
  /** Maximum selectable date. Months after this are disabled. Defaults to now. */
  maxDate?: Date
} & React.ComponentProps<"div">

export function MonthPickerCalendar({
  year,
  month,
  onSelectMonth,
  onYearChange,
  minDate,
  maxDate,
  className,
  ...props
}: MonthPickerCalendarProps) {
  const [pickerYear, setPickerYear] = useState(year)

  const maxDayjs = maxDate ? dayjs(maxDate) : dayjs()
  const minDayjs = minDate ? dayjs(minDate) : null

  const MONTHS_SHORT = Array.from({ length: 12 }, (_, i) => dayjs().month(i).format("MMM"))

  const canGoToPreviousYear = minDayjs ? pickerYear > minDayjs.year() : true
  const canGoToNextYear = pickerYear < maxDayjs.year()

  const handleYearNav = (delta: number) => {
    const newYear = pickerYear + delta
    setPickerYear(newYear)
    onYearChange?.(newYear)
  }

  return (
    <div className={cn("flex flex-col gap-3", className)} {...props}>
      {/* Year navigation */}
      <div className="flex items-center justify-between">
        <button
          className="rounded-full p-1 hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
          onClick={() => handleYearNav(-1)}
          disabled={!canGoToPreviousYear}
        >
          <ChevronLeft className="size-5" />
        </button>
        <Text fontStyle="BM">{pickerYear}</Text>
        <button
          className="rounded-full p-1 hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
          onClick={() => handleYearNav(1)}
          disabled={!canGoToNextYear}
        >
          <ChevronRight className="size-5" />
        </button>
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-4 gap-2">
        {MONTHS_SHORT.map((name, index) => {
          const isSelected = month === index && year === pickerYear
          const monthDate = dayjs().year(pickerYear).month(index)
          const isFuture = monthDate.isAfter(maxDayjs, "month")
          const isBeforeMin = minDayjs ? monthDate.isBefore(minDayjs, "month") : false
          const isDisabled = isFuture || isBeforeMin

          return (
            <button
              key={name}
              className={cn(
                "rounded-full px-2 py-2 text-sm hover:bg-primary-500",
                isSelected && "bg-primary-500 hover:bg-primary-600",
                isDisabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
              )}
              onClick={() => !isDisabled && onSelectMonth(index)}
              disabled={isDisabled}
            >
              <Text fontStyle="BM">{name}</Text>
            </button>
          )
        })}
      </div>
    </div>
  )
}
